import { type JSX, useCallback, useEffect, useState } from 'react';

import { type Benachrichtigung, erledigt, gelesen, meine } from '../api/benachrichtigungen';

/** Ortszeit (FRS-01, LOG-03). Die Zone steht fest und kommt nicht aus dem Browser. */
const ZEIT = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Berlin',
});

/**
 * Die Liste „Benachrichtigungen" (ERR-10; Auftrag CRM-207).
 *
 * „Das steht an" zählt die Nachrichten je Anlass; hier stehen sie einzeln —
 * mit Text, Zeit, dem Sprung in ihren Bereich und den beiden Handgriffen:
 * „Gelesen" (gesehen, steht weiter an) und „Erledigt" (fällt heraus).
 *
 * NACH JEDEM HANDGRIFF NEU GEHOLT. Eine Nachricht an eine Rolle kann ein
 * Kollege zur selben Zeit erledigen; die Liste zeigt, was der Server weiß.
 */
export function Benachrichtigungsliste({
  beiBereich,
}: {
  readonly beiBereich: (kennung: string) => void;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Benachrichtigung[] | undefined>(undefined);
  const [stoerung, setzeStoerung] = useState<string | undefined>(undefined);
  /** Welcher Eintrag gerade läuft — gegen den zweiten Druck. */
  const [laeuft, setzeLaeuft] = useState<string | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeListe((await meine()).nachrichten);
    } catch {
      setzeStoerung('Die Benachrichtigungen konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const handgriff = async (id: string, was: (id: string) => Promise<void>): Promise<void> => {
    setzeLaeuft(id);
    setzeStoerung(undefined);
    try {
      await was(id);
    } catch {
      setzeStoerung('Das hat nicht geklappt. Die Liste ist neu geladen.');
    }
    await laden();
    setzeLaeuft(undefined);
  };

  if (liste === undefined) {
    return stoerung === undefined ? (
      <p role="status">Benachrichtigungen werden geladen …</p>
    ) : (
      <p role="alert">{stoerung}</p>
    );
  }
  return (
    <div className="benachrichtigungen">
      {stoerung !== undefined && <p role="alert">{stoerung}</p>}
      {liste.length === 0 ? (
        <p>Keine offenen Benachrichtigungen.</p>
      ) : (
        <ul className="benachrichtigungen__liste" aria-label="Benachrichtigungen">
          {liste.map((n) => (
            <li
              key={n.id}
              className={`benachrichtigung${n.gelesenAm === null ? ' benachrichtigung--neu' : ''}`}
            >
              <p className="benachrichtigung__text">{n.text}</p>
              <p className="benachrichtigung__zeit">
                <time dateTime={n.entstandenAm}>{ZEIT.format(new Date(n.entstandenAm))}</time>
                {n.persoenlich ? ' · an Sie' : ''}
                {n.gelesenAm !== null ? ' · gelesen' : ''}
              </p>
              <div className="benachrichtigung__knoepfe">
                <button className="knopf knopf--klein" type="button" onClick={() => beiBereich(n.bereich)}>
                  Öffnen
                </button>
                {n.gelesenAm === null && (
                  <button
                    className="knopf knopf--klein"
                    type="button"
                    disabled={laeuft === n.id}
                    onClick={() => void handgriff(n.id, gelesen)}
                  >
                    Gelesen
                  </button>
                )}
                <button
                  className="knopf knopf--klein"
                  type="button"
                  disabled={laeuft === n.id}
                  onClick={() => void handgriff(n.id, erledigt)}
                >
                  Erledigt
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
